import { PlayerState, ELIMINATION_THRESHOLD } from '../types';

// 対戦ペア
export interface MatchPairing {
  player1Id: string;
  player2Id: string;
}

// トーナメント設定
export interface TournamentConfig {
  qualifyingRounds: number;    // 予選ラウンド数 (デフォルト7)
  finalistsCount: number;      // 決勝進出者数 (デフォルト2)
  eliminationEnabled: boolean; // 敗退ルール有効
  fansLossOnLose: number;      // 敗北時のファン減少量
}

export const DEFAULT_TOURNAMENT_CONFIG: TournamentConfig = {
  qualifyingRounds: 7,
  finalistsCount: 2,
  eliminationEnabled: true,
  fansLossOnLose: 1,
};

// ファン獲得テーブル (ラウンド別)
// Challengers準拠: 序盤は少なく、終盤は多い
const FANS_TABLE: Record<number, { win: number; flagBonus: number }> = {
  1: { win: 1, flagBonus: 0 },
  2: { win: 1, flagBonus: 0 },
  3: { win: 2, flagBonus: 1 },
  4: { win: 2, flagBonus: 1 },
  5: { win: 3, flagBonus: 1 },
  6: { win: 3, flagBonus: 2 },
  7: { win: 4, flagBonus: 2 },
  8: { win: 5, flagBonus: 3 }, // Finals
};

export class TournamentManager {
  private config: TournamentConfig;
  private matchHistory: Map<string, Set<string>> = new Map(); // playerId -> 対戦相手IDのSet

  constructor(config: Partial<TournamentConfig> = {}) {
    this.config = { ...DEFAULT_TOURNAMENT_CONFIG, ...config };
  }

  // ============================================
  // Swiss式ペアリング
  // 1. ファン数が近いプレイヤー同士でマッチング
  // 2. 既に対戦した相手は避ける
  // 3. 敗退プレイヤーは除外
  // ============================================
  generatePairings(players: PlayerState[], round: number): MatchPairing[] {
    // アクティブプレイヤーのみ
    const activePlayers = players.filter((p) => !p.isEliminated);
    
    if (activePlayers.length < 2) {
      return [];
    }

    // ファン数でソート (降順)
    const sorted = [...activePlayers].sort((a, b) => b.fans - a.fans);
    
    const pairings: MatchPairing[] = [];
    const paired = new Set<string>();

    for (const player of sorted) {
      if (paired.has(player.info.id)) continue;

      // 最適な対戦相手を探す
      const opponent = this.findBestOpponent(player, sorted, paired);
      
      if (opponent) {
        pairings.push({
          player1Id: player.info.id,
          player2Id: opponent.info.id,
        });
        paired.add(player.info.id);
        paired.add(opponent.info.id);
        
        // 対戦履歴を記録
        this.recordMatch(player.info.id, opponent.info.id);
      }
    }

    // 奇数の場合、最後の1人はByeとして勝利扱い
    const unpaired = sorted.filter((p) => !paired.has(p.info.id));
    if (unpaired.length === 1) {
      // Byeプレイヤーにはボーナスファンを与える
      unpaired[0].fans += 1;
    }

    return pairings;
  }

  // 最適な対戦相手を探す
  private findBestOpponent(
    player: PlayerState,
    candidates: PlayerState[],
    excluded: Set<string>
  ): PlayerState | null {
    const playerHistory = this.matchHistory.get(player.info.id) ?? new Set();

    // 優先度: ファン差が小さく、まだ対戦していない相手
    const available = candidates.filter((c) => 
      c.info.id !== player.info.id &&
      !excluded.has(c.info.id) &&
      !playerHistory.has(c.info.id)
    );

    if (available.length > 0) {
      // ファン数が最も近い相手
      available.sort((a, b) => 
        Math.abs(a.fans - player.fans) - Math.abs(b.fans - player.fans)
      );
      return available[0];
    }

    // 全員と対戦済みの場合、ファン差が最も小さい相手
    const anyAvailable = candidates.filter((c) => 
      c.info.id !== player.info.id && !excluded.has(c.info.id)
    );
    
    if (anyAvailable.length > 0) {
      anyAvailable.sort((a, b) => 
        Math.abs(a.fans - player.fans) - Math.abs(b.fans - player.fans)
      );
      return anyAvailable[0];
    }

    return null;
  }

  // 対戦履歴を記録
  private recordMatch(player1Id: string, player2Id: string): void {
    if (!this.matchHistory.has(player1Id)) {
      this.matchHistory.set(player1Id, new Set());
    }
    if (!this.matchHistory.has(player2Id)) {
      this.matchHistory.set(player2Id, new Set());
    }
    this.matchHistory.get(player1Id)!.add(player2Id);
    this.matchHistory.get(player2Id)!.add(player1Id);
  }

  // 過去に対戦したかチェック
  hasPlayedBefore(player1Id: string, player2Id: string): boolean {
    return this.matchHistory.get(player1Id)?.has(player2Id) ?? false;
  }

  // ============================================
  // ファン計算
  // ============================================
  calculateFansGained(round: number, flagsWon: number): number {
    const table = FANS_TABLE[round] ?? FANS_TABLE[7];
    return table.win + (flagsWon > 3 ? table.flagBonus : 0);
  }

  calculateFansLost(round: number): number {
    return this.config.fansLossOnLose;
  }

  // ============================================
  // 敗退処理
  // ============================================
  checkElimination(player: PlayerState, round: number): boolean {
    if (!this.config.eliminationEnabled) return false;

    // ファンが閾値以下で敗退
    if (player.fans < ELIMINATION_THRESHOLD && round >= 3) {
      player.isEliminated = true;
      player.eliminatedRound = round;
      return true;
    }

    return false;
  }

  // アクティブプレイヤー数
  getActivePlayerCount(players: PlayerState[]): number {
    return players.filter((p) => !p.isEliminated).length;
  }

  // ============================================
  // 決勝進出者決定
  // ============================================
  determinateFinalists(players: PlayerState[]): PlayerState[] {
    const activePlayers = players.filter((p) => !p.isEliminated);
    
    // ファン数でソート
    const sorted = [...activePlayers].sort((a, b) => {
      // 1. ファン数 (降順)
      if (b.fans !== a.fans) return b.fans - a.fans;
      
      // 2. 勝利数 (降順)
      const aWins = a.matchHistory.filter((m) => m.result === 'win').length;
      const bWins = b.matchHistory.filter((m) => m.result === 'win').length;
      if (bWins !== aWins) return bWins - aWins;
      
      // 3. 直接対決
      const directMatch = a.matchHistory.find((m) => m.opponentId === b.info.id);
      if (directMatch) {
        return directMatch.result === 'win' ? -1 : 1;
      }
      
      return 0;
    });

    return sorted.slice(0, this.config.finalistsCount);
  }

  // ============================================
  // ランキング生成
  // ============================================
  generateRanking(players: PlayerState[]): { rank: number; player: PlayerState; status: string }[] {
    const sorted = [...players].sort((a, b) => {
      // 敗退者は下位
      if (a.isEliminated && !b.isEliminated) return 1;
      if (!a.isEliminated && b.isEliminated) return -1;
      
      // ファン数でソート
      if (b.fans !== a.fans) return b.fans - a.fans;
      
      // 勝利数でソート
      const aWins = a.matchHistory.filter((m) => m.result === 'win').length;
      const bWins = b.matchHistory.filter((m) => m.result === 'win').length;
      return bWins - aWins;
    });

    return sorted.map((player, idx) => ({
      rank: idx + 1,
      player,
      status: player.isEliminated 
        ? `R${player.eliminatedRound}敗退` 
        : 'アクティブ',
    }));
  }

  // 設定取得
  getConfig(): TournamentConfig {
    return { ...this.config };
  }

  // 予選ラウンド数
  getQualifyingRounds(): number {
    return this.config.qualifyingRounds;
  }

  // 対戦履歴リセット
  reset(): void {
    this.matchHistory.clear();
  }
}
